import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from "../../components/ui/pagination";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { cn } from "../../lib/utils";
import { ChevronLeft, ChevronRight, Eye, ExternalLink, Search } from "lucide-react";
import { formatCurrencyINR } from "../../lib/enhanced-currency-utils";

interface AssetTableProps {
  onViewDetails: (asset: any) => void;
  pagination?: {
    totalRecords: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
  };
  onPageChange?: (page: number, pageSize: number) => void;
  loading?: boolean;
  assets?: any[];
}

const statusStyles: Record<string, string> = {
  delivered:
    "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-600 hover:text-white",
  pending:
    "bg-amber-50 text-amber-600 border-amber-100 hover:bg-amber-600 hover:text-white",
  partial:
    "bg-orange-50 text-orange-600 border-orange-100 hover:bg-orange-600 hover:text-white",
};

const formatDateOnly = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toISOString().split("T")[0];
  } catch {
    return dateString;
  }
};

export const AssetTable = ({
  onViewDetails,
  pagination,
  onPageChange,
  loading = false,
  assets = [],
}: AssetTableProps) => {
  const totalPages = pagination?.totalPages ?? 1;

  return (
    <div className="overflow-hidden rounded-[20px] border border-slate-200 bg-white">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-slate-200 bg-slate-50/70 hover:bg-slate-50/70">
              <TableHead className="w-[120px] px-4 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Asset ID
              </TableHead>
              <TableHead className="w-[140px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                PO ID
              </TableHead>
              <TableHead className="w-[100px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Supplier
              </TableHead>
              <TableHead className="w-[100px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Customer
              </TableHead>
              <TableHead className="w-[100px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Asset Item
              </TableHead>
              <TableHead className="w-[100px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Total Value
              </TableHead>
              <TableHead className="w-[100px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Last Updated
              </TableHead>
              <TableHead className="w-[140px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Insurance End Date
              </TableHead>
              <TableHead className="w-[80px] px-2 text-center align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Delivery Status
              </TableHead>
              <TableHead className="w-[100px] px-2 text-center align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} className="py-16">
                  <div className="flex items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-[#3f8b98]" />
                  </div>
                </TableCell>
              </TableRow>
            ) : assets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="py-20">
                  <div className="flex flex-col items-center justify-center text-slate-400">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-50">
                      <Search className="h-8 w-8 opacity-20" />
                    </div>
                    <p className="text-sm font-bold uppercase tracking-wider text-slate-500">
                      No assets found
                    </p>
                    <p className="mt-1 text-[11px]">Try adjusting your search query</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              assets.map((asset, index) => (
                <TableRow
                  key={index}
                  className="border-b border-slate-100 hover:bg-slate-50/40"
                >
                  <TableCell className="py-3.5 pl-3 text-[13px] font-semibold text-slate-800">
                    {asset.unique_asset_id}
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => onViewDetails(asset)}
                      className="inline-flex items-center gap-0 text-left text-[13px] font-semibold text-[#2f93b4] transition-colors hover:text-[#25788f]"
                    >
                      {asset.purchase_order_code}
                      <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  </TableCell>
                  <TableCell className="text-[13px] font-medium text-slate-700">
                    {asset.supplierTradeName || asset.supplierName}
                  </TableCell>
                  <TableCell className="text-[13px] font-medium text-slate-700">
                    {asset.customerBrandName || asset.customerName}
                  </TableCell>
                  <TableCell className="max-w-xs text-[13px] font-medium text-slate-700">
                    {asset.item}
                  </TableCell>
                  <TableCell className="text-[13px] font-semibold text-slate-800">
                    {formatCurrencyINR(
                      asset.unit_price + (asset.unit_price * (asset.tax_rate || 0)) / 100
                    )}
                  </TableCell>
                  <TableCell className="text-[13px] font-medium text-slate-500">
                    {formatDateOnly(asset.updated_at)}
                  </TableCell>
                  <TableCell className="text-[13px] font-medium">
                    {asset.insurance_end_date ? (
                      <span
                        className={cn(
                          new Date(asset.insurance_end_date) < new Date()
                            ? "text-red-500"
                            : "text-slate-700"
                        )}
                      >
                        {formatDateOnly(asset.insurance_end_date)}
                      </span>
                    ) : (
                      <span className="text-amber-600">Pending</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={cn(
                        "rounded-full border px-3 py-1 text-[11px] font-semibold shadow-none transition-colors duration-200",
                        statusStyles[asset.status.toLowerCase()] || statusStyles.pending
                      )}
                    >
                      {asset.status.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell className="pr-5 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewDetails(asset)}
                      className="h-10 rounded-xl border-slate-200 bg-white px-4 text-[13px] font-medium text-slate-700 shadow-none transition-colors hover:bg-slate-50"
                    >
                      <Eye className="mr-2 h-4 w-4 text-slate-400" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && totalPages > 1 && (
        <div className="flex flex-col gap-3 border-t border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-4">
          <div className="text-[12px] font-medium text-slate-500 sm:text-[13px]">
            Showing {(pagination.currentPage - 1) * pagination.pageSize + 1} to{" "}
            {Math.min(
              pagination.currentPage * pagination.pageSize,
              pagination.totalRecords
            )}{" "}
            of {pagination.totalRecords} entries
          </div>

          <Pagination className="mx-0 w-auto justify-end">
            <PaginationContent className="gap-1.5">
              <PaginationItem>
                <button
                  type="button"
                  onClick={() =>
                    onPageChange?.(Math.max(1, pagination.currentPage - 1), pagination.pageSize)
                  }
                  className={cn(
                    "inline-flex h-8 items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 text-[12px] font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900",
                    pagination.currentPage === 1 && "pointer-events-none opacity-50"
                  )}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Prev
                </button>
              </PaginationItem>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (pagination.currentPage <= 3) pageNum = i + 1;
                else if (pagination.currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = pagination.currentPage - 2 + i;
                }

                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      onClick={() => onPageChange?.(pageNum, pagination.pageSize)}
                      isActive={pagination.currentPage === pageNum}
                      className={cn(
                        "flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border p-0 text-[13px] font-bold transition-colors",
                        pagination.currentPage === pageNum
                          ? "border-[#3f8b98] bg-[#3f8b98] text-white hover:bg-[#3f8b98] hover:text-white"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      )}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              <PaginationItem>
                <button
                  type="button"
                  onClick={() =>
                    onPageChange?.(Math.min(totalPages, pagination.currentPage + 1), pagination.pageSize)
                  }
                  className={cn(
                    "inline-flex h-8 items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 text-[12px] font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900",
                    pagination.currentPage === totalPages && "pointer-events-none opacity-50"
                  )}
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};
